import { useState } from 'react'
import { STORAGE_KEYS_PREFIX } from 'core/constants'

interface UseLocalStorage<T> {
  key: string
  initialValue: T
}

type LocalStorageResult<T> = [T, (newValue: T) => void, () => void]

/**
 * @hooks
 * @name useLocalStorage
 * @description 로컬스토리지의 값 저장, 수정, 삭제 할 수 있는 훅
 * @property {string} key 로컬스토리지에 저장할 Key (Required)
 * @property {T} initialValue 로컬스토리지에 저장할 초기값 (Required)
 * @returns {LocalStorageResult} [value, setLocalStorage, removeLocalStorage]
 * @reference [공식문서 useClientRect](https://ko.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node)
 * @example
 * const [storedValue, setStoredValue, removeStoredValue] =
    useLocalStorage<string>({ key: 'value', initialValue: '' })
 */

const useLocalStorage = <T>({ key, initialValue }: UseLocalStorage<T>): LocalStorageResult<T> => {
  const storageKey = STORAGE_KEYS_PREFIX + key
  const storedItem = localStorage.getItem(storageKey)

  const [value, setValue] = useState<T>(storedItem !== null ? JSON.parse(storedItem) : initialValue)

  const setLocalStorage = (newValue: T) => {
    setValue(newValue)
    localStorage.setItem(storageKey, JSON.stringify(newValue))
  }

  const removeLocalStorage = () => {
    setValue(initialValue)
    localStorage.removeItem(storageKey)
  }

  return [value, setLocalStorage, removeLocalStorage]
}

export default useLocalStorage
